import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { dealApi, dealQueryKeys } from '@crm/entities/deal';
import { dealsBoardQueryKeys } from '@crm/entities/deals-board';
import { toastManager } from '@crm/entities/toast';
import { getApiErrorMessage } from '@crm/shared/api';

import type { TInWorkOrFinishDealInvalidationScope } from '../model';
import type { TDeal } from '@crm/entities/deal';

type TInWorkOrFinishDealMutationVariables = {
    dealIds: readonly TDeal['id'][];
};

export const useInWorkOrFinishDealMutation = ({
    invalidationScope,
}: {
    invalidationScope: TInWorkOrFinishDealInvalidationScope;
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const invalidateRelatedQueries = async (dealIds: readonly TDeal['id'][]) => {
        const sourceQueryKeys: Record<
            TInWorkOrFinishDealInvalidationScope,
            readonly unknown[] | null
        > = {
            board: null,
            lost: dealQueryKeys.lostDeals.all(),
            postponed: dealQueryKeys.postponedDeals.all(),
        };
        const sourceQueryKey = sourceQueryKeys[invalidationScope];

        const queryKeys = [
            ...dealIds.map(dealQueryKeys.detail),
            dealQueryKeys.inWorkOrFinishedDeals.all(),
            ...(sourceQueryKey ? [sourceQueryKey] : []),
        ];

        await Promise.all([
            ...queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
            queryClient.invalidateQueries({
                queryKey: dealsBoardQueryKeys.board(),
                exact: true,
                refetchType: 'all',
            }),
        ]);
    };

    return useMutation({
        mutationKey: ['deal', 'in-work-or-finished'],
        mutationFn: ({ dealIds }: TInWorkOrFinishDealMutationVariables) => {
            if (dealIds.length === 0 || new Set(dealIds).size !== dealIds.length) {
                throw new Error(t('crm.common.deal.errors.invalidIds'));
            }

            return dealApi.markDealsInWorkOrFinished({ dealIds });
        },
        onSuccess: async (_, { dealIds }) => {
            await invalidateRelatedQueries(dealIds);

            toastManager.onSuccess({
                title:
                    dealIds.length > 1
                        ? t('crm.inWorkOrFinishDeal.notifications.success.batch', {
                              count: dealIds.length,
                          })
                        : t('crm.inWorkOrFinishDeal.notifications.success.single'),
            });
        },
        onError: (error) => {
            toastManager.onError({
                title: getApiErrorMessage(error, t('crm.inWorkOrFinishDeal.errors.submit')),
            });
        },
    });
};
